import type { V2ChatPayload, V2ConversationResponse, V2ResponseState } from "@/types/v2";
import { useToast } from "vue-toast-notification";
import { useI18n } from "vue3-i18n";

export function useV2ConversationRequest() {
  const v2ResponseStore = useV2ResponseStore();
  const queryClient = useQueryClient();
  const { toggleCurrentRoute } = useV2Layout();
  const toast = useToast(defaultToastOptions);
  const { t } = useI18n();

  const request = useMutation({
    mutationKey: ["v2", "conversation"],
    mutationFn: async (data: V2ChatPayload) => {
      // "/v2/chat";
      const payload = { ...data };
      if (v2ResponseStore.isNewConversation) {
        payload.newChat = true;
        payload.dialogueId = undefined;
      }
      const response = await usePostRequest<V2ConversationResponse>("/hey", payload);
      return response.data || null;
    },

    onMutate(payload) {
      // Optimistic updates
      const conversationId = payload.dialogueId;
      const text = formatInput(payload.inputContent || []);
      toggleCurrentRoute(false);
      v2ResponseStore.toggleRecentConversations(false);
      const messageIndex = v2ResponseStore.addMessage(
        {
          errors: [],
          fields: [],
          hints: [],
          statement: text,
          step: "input",
        },
        "user"
      );
      v2ResponseStore.isPreviewDismissed = false;

      if (conversationId) {
        addMessageToConversationDetails(
          conversationId,
          {
            content: text,
            role: "user",
            created: new Date().toISOString(),
          },
          queryClient
        );
      }

      return {
        oldConversationId: conversationId,
        plainTextInput: text,
        messageIndex,
      };
    },
    onSettled() {
      v2ResponseStore.isNewConversation = false;
    },
    onError(e, _payload, context) {
      const { message } = e;
      const oldConversationId = context?.oldConversationId;
      if (message && message.includes("timeout")) {
        if (oldConversationId) v2ResponseStore.addTimedOutConversation(oldConversationId);
      } else {
        // clean up and restore message in input area
        toast.error(t("errors.tryAgain"));
        if (oldConversationId) {
          removeLastMessageFromConversationDetails(oldConversationId, queryClient);
        }
        if (context?.messageIndex !== undefined) {
          v2ResponseStore.removeMessage(context.messageIndex);
        }
        if (
          v2ResponseStore.lastMessage?.role === "user" &&
          v2ResponseStore.lastMessage?.content?.result === context?.plainTextInput
        ) {
          v2ResponseStore.lastMessage = null;
        }
        sendTextToEditor(context?.plainTextInput || "");
      }
    },
  });

  return request;
}

//
// UTILS
//
function formatInput(blocks: V2ChatPayload["inputContent"]) {
  if (!blocks) return "";
  return blocks
    .map((b: any) => b.data?.text)
    .filter(t => !!t)
    .join("");
}

export function useV2ConversationSocketHandler() {
  const queryClient = useQueryClient();
  const v2ResponseStore = useV2ResponseStore();
  const v2InputStore = useV2InputStore();

  function handleV2ConversationMessageEvent(data: V2ResponseState) {
    const { dialogueId = "", finalMessage = "" } = data;
    const oldConversationId = v2ResponseStore.lastConversationId;

    // clean up processing state
    const isNew = v2ResponseStore.isNewConversation;
    if (isNew || !oldConversationId) v2ResponseStore.dismissProcessingConversation("new");
    v2ResponseStore.dismissProcessingConversation(dialogueId);
    const processingCounts = Object.values(v2ResponseStore.processingConversations || {});
    if (processingCounts.length === 0 || processingCounts.every(v => !v)) {
      v2InputStore.currentStateHelpers.setDone();
    }

    if (!dialogueId) return;
    v2ResponseStore.lastConversationId = dialogueId;
    v2ResponseStore.addMessage(
      {
        statement: finalMessage,
        step: "output",
        fields: [],
        hints: [],
        errors: [],
      },
      "assistant"
    );

    // Active, open conversation
    const existing = getCachedConversationDetailsById(dialogueId, queryClient);

    if (existing) {
      addMessageToConversationDetails(
        dialogueId,
        {
          content: finalMessage,
          role: "assistant",
          created: new Date().toISOString(),
        },
        queryClient
      );
    } else {
      prefetchSingleMessageDetails(dialogueId, queryClient);
    }

    if ((dialogueId && !oldConversationId) || v2ResponseStore.isNewConversation) {
      // backend auto-assigned conversation and we have it cached?
      queryClient.invalidateQueries({
        queryKey: ["v2", "conversations", "recent"],
      });
    }
  }

  return { handleV2ConversationMessageEvent };
}
